'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { HmrcTokenExpiredError, useHMRCITSAStore } from '@repo/store/useHMRCITSAStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { isValidNI } from '@repo/validation/isValidNI'
import { TextEntry } from '@repo/ui/TextEntry'
import { StepIndicator } from '../StepIndicator/StepIndicator'
import { NINumberField } from '../NINumberField/NINumberField'
import { FiguresReviewTable } from '../FiguresReviewTable/FiguresReviewTable'
import { FraudHeaderValidationToast } from '../FraudHeaderValidationToast/FraudHeaderValidationToast'
import { deriveTaxYear } from '../dateUtils'
import type {
  AnnualAdjustments,
  AnnualAllowances,
  AnnualNonFinancials,
  Business,
  ItsaBusinessObligation,
  ObligationStatus,
  FraudHeaderValidationSummary
} from '../types'
import {
  ADJUSTMENT_FIELDS,
  ALLOWANCE_FIELDS,
  HMRC_SETUP_ROUTE_PATH,
  OBLIGATION_STATUS_FILTER_OPTIONS,
  buildFigureRows,
  buildInitialNonFinancials,
  buildZeroAdjustments,
  buildZeroAllowances,
  getApiErrorMessage,
  isValidationError
} from './constants'
import MUIAlert from '@mui/material/Alert'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControl from '@mui/material/FormControl'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUISelect from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const STEP_NI = 0
const STEP_BUSINESS = 1
const STEP_OBLIGATION = 2
const STEP_ADJUSTMENTS = 3
const STEP_REVIEW = 4

const STEPS = ['National Insurance number', 'Select business', 'Select obligation', 'Adjustments and allowances', 'Review figures']

const GENERIC_BUSINESSES_ERROR = 'An error occurred while fetching businesses. Please try again.'
const GENERIC_ELIGIBILITY_ERROR = 'An error occurred while checking this business. Please try again.'
const GENERIC_OBLIGATIONS_ERROR = 'An error occurred while fetching obligations. Please try again.'
const GENERIC_MINT_ERROR = 'An error occurred while preparing the acknowledgement. Please try again.'
const GENERIC_SUBMIT_ERROR = 'An error occurred while submitting to HMRC. Please try again.'

// FR-08 acknowledgement copy — the checkbox is a client-side UX gate only; the server
// trusts the acknowledgementToken minted by PrepareAnnualSummaryAcknowledgement (BE9b), not
// this flag (ADR 0031).
const ACKNOWLEDGEMENT_TEXT =
  'I confirm that these figures are complete and correct, that the categorisation applied is correct, and I understand that I am responsible for this submission to HMRC.'

// HMRC fraud-prevention-headers Terms of Use: users must be told what device/connection
// data is collected and sent. This checkbox is a NOTICE gate only — the lawful basis for
// sending the headers is legal obligation, not consent, so the tick is deliberately NOT
// stored anywhere (storing it would imply revocable consent semantics that do not apply).
const FRAUD_PREVENTION_NOTICE_TEXT =
  'I understand that Osprey collects information about my device and connection (device identifier, screen and window details, timezone, browser details and IP address) and sends it to HMRC with this submission, as required by UK law for fraud prevention.'

const optionBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '6px',
  padding: '8px 16px',
  marginBottom: 1,
  marginLeft: 0,
  width: '100%'
}

// The annual submission wizard (FE8, route wired by FE11 at reports/itsa-mtd-annual — see
// ReportModule's NEW_SUBMISSION_ROUTE_PATH-adjacent comment). ADR 0031 Model 2 (annual
// path): unlike QuarterlySubmissionModule (FE7a/FE7b) there is NO annual Calculate — the
// figures are entered manually here, then POSTed to BE9b's PrepareAnnualSummaryAcknowledgement
// to mint an acknowledgementToken bound to those EXACT figures, before the acknowledge
// checkbox can gate the submit-to-HMRC action (BE6 CreateSelfEmploymentAnnualSummary, which
// verifies the token against the same figures via a figuresHash check).
// Prototype UI direction: johntaylor:apps/spa_osprey/src/pages/configuration/hmrc-itsa/hmrc-itsa-annual-form.jsx.
export const AnnualSubmissionModule = () => {
  const {
    getBusinessesByNationalInsuranceNumber,
    getBusinessExtended,
    getSelfEmploymentBusinessObligations,
    prepareAnnualSummaryAcknowledgement,
    createSelfEmploymentAnnualSummary
  } = useHMRCITSAStore()
  // Estate pattern (see CaseTypeMappingModule): self-supplied top clearance when the layout
  // hides the PageTitleBlock on reduced-height windows (the breadcrumb bar is position:fixed).
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const navigate = useNavigate()

  const [step, setStep] = useState(STEP_NI)
  const [nationalInsuranceNumber, setNationalInsuranceNumber] = useState('')
  const [businesses, setBusinesses] = useState<Business[]>([])
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [obligationStatusFilter, setObligationStatusFilter] = useState<ObligationStatus | ''>('')
  const [obligations, setObligations] = useState<ItsaBusinessObligation[]>([])
  const [selectedObligationIndex, setSelectedObligationIndex] = useState('')

  const [adjustments, setAdjustments] = useState<AnnualAdjustments>(buildZeroAdjustments())
  const [allowances, setAllowances] = useState<AnnualAllowances>(buildZeroAllowances())
  const [nonFinancials, setNonFinancials] = useState<AnnualNonFinancials>(buildInitialNonFinancials())

  // The exact figures a token was minted for — sent unchanged to the submit call, since
  // BE6 rejects the submission if the posted figures don't hash-match the token.
  const [mintedAdjustments, setMintedAdjustments] = useState<AnnualAdjustments | null>(null)
  const [mintedAllowances, setMintedAllowances] = useState<AnnualAllowances | null>(null)
  const [mintedNonFinancials, setMintedNonFinancials] = useState<AnnualNonFinancials | null>(null)
  const [acknowledgementToken, setAcknowledgementToken] = useState<string | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [tokenExpired, setTokenExpired] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [eligibilityError, setEligibilityError] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [fraudPreventionNoticeAccepted, setFraudPreventionNoticeAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [submissionSucceeded, setSubmissionSucceeded] = useState(false)
  // RF14 (FPH-ACC11/FE6): non-prod fraud-header validation feedback carried on the submit
  // response — null in production (server-side guard), non-null in non-prod only.
  const [fraudHeaderValidation, setFraudHeaderValidation] = useState<FraudHeaderValidationSummary | null>(null)

  const isValidNumber = isValidNI(nationalInsuranceNumber)
  const selectedBusiness = businesses.find((business) => business.businessId === selectedBusinessId) || null
  const selectedObligation =
    selectedObligationIndex !== '' ? obligations[Number(selectedObligationIndex)] : null

  const handleReconnect = () => {
    navigate(HMRC_SETUP_ROUTE_PATH, { state: { tokenExpired: true } })
  }

  const loadObligations = async (filter: ObligationStatus | '') => {
    setErrorMessage('')
    setSelectedObligationIndex('')
    setIsLoading(true)
    try {
      const result = await getSelfEmploymentBusinessObligations({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        obligationStatus: filter || undefined
      })
      setObligations((result as ItsaBusinessObligation[]) || [])
    } catch (error) {
      setObligations([])
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setErrorMessage(getApiErrorMessage(error, GENERIC_OBLIGATIONS_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleNiNext = async () => {
    setErrorMessage('')
    setTokenExpired(false)
    setIsLoading(true)
    try {
      const result = await getBusinessesByNationalInsuranceNumber({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase()
      })
      if (result && result.length > 0) {
        setBusinesses(result as Business[])
        setStep(STEP_BUSINESS)
      } else {
        setErrorMessage('No self-employment businesses were found for this National Insurance number.')
      }
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setErrorMessage(getApiErrorMessage(error, GENERIC_BUSINESSES_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleBusinessBack = () => {
    setErrorMessage('')
    setEligibilityError('')
    setStep(STEP_NI)
  }

  // BE4's ItsaFirmEligibilityGuard runs on GetBusinessExtended and rejects with a 400
  // ValidationException for a non-self-employment firm type — surfaced here as an inline
  // validation message distinct from the generic error banner, and it blocks progression.
  const handleBusinessNext = async () => {
    setErrorMessage('')
    setEligibilityError('')
    setTokenExpired(false)
    setIsLoading(true)
    try {
      await getBusinessExtended({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId
      })
      setStep(STEP_OBLIGATION)
      await loadObligations(obligationStatusFilter)
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else if (isValidationError(error)) setEligibilityError(getApiErrorMessage(error, GENERIC_ELIGIBILITY_ERROR))
      else setErrorMessage(getApiErrorMessage(error, GENERIC_ELIGIBILITY_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleObligationBack = () => {
    setErrorMessage('')
    setStep(STEP_BUSINESS)
  }

  const handleObligationFilterChange = (newFilter: ObligationStatus | '') => {
    setObligationStatusFilter(newFilter)
    loadObligations(newFilter)
  }

  // No annual Calculate (ADR 0031) — selecting an obligation only fixes the period this
  // annual summary covers (its periodEndDate derives the tax year); it proceeds straight
  // to the manual adjustments/allowances entry step.
  const handleObligationNext = () => {
    if (!selectedObligation) return
    setErrorMessage('')
    setStep(STEP_ADJUSTMENTS)
  }

  const handleAdjustmentsBack = () => {
    setErrorMessage('')
    setStep(STEP_OBLIGATION)
  }

  const updateAdjustment = (key: keyof AnnualAdjustments, value: number | null) => {
    setAdjustments((prev) => ({ ...prev, [key]: value ?? 0 }))
  }

  const updateAllowance = (key: keyof AnnualAllowances, value: number | null) => {
    setAllowances((prev) => ({ ...prev, [key]: value ?? 0 }))
  }

  // Mints the acknowledgementToken (BE9b) for the exact figures just assembled — the SAME
  // adjustments/allowances/nonFinancials object graph is retained (mintedXxx) and carried
  // unchanged through to the eventual submit, since BE6 verifies the token against a
  // figuresHash of those figures.
  const handleAdjustmentsNext = async () => {
    setErrorMessage('')
    setIsLoading(true)
    try {
      const response = await prepareAnnualSummaryAcknowledgement({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        annualSummary: { adjustments, allowances, nonFinancials }
      })
      setMintedAdjustments(adjustments)
      setMintedAllowances(allowances)
      setMintedNonFinancials(nonFinancials)
      setAcknowledgementToken((response && response.acknowledgementToken) || null)
      setAcknowledged(false)
      setFraudPreventionNoticeAccepted(false)
      setSubmitError('')
      setSubmissionSucceeded(false)
      setStep(STEP_REVIEW)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, GENERIC_MINT_ERROR))
    } finally {
      setIsLoading(false)
    }
  }

  const handleReviewBack = () => {
    setErrorMessage('')
    setSubmitError('')
    setAcknowledged(false)
    setFraudPreventionNoticeAccepted(false)
    setStep(STEP_ADJUSTMENTS)
  }

  // ADR 0031 (Model 2): the acknowledgementToken minted by PrepareAnnualSummaryAcknowledgement
  // is threaded straight through to the submit — never fabricated or omitted; it is what
  // BE6 verifies against the figures being filed. userAcknowledged mirrors the checkbox
  // (audit only) — the server trusts the token, not this flag.
  const handleSubmit = async () => {
    if (!selectedObligation || !mintedAdjustments || !mintedAllowances || !mintedNonFinancials || !acknowledgementToken)
      return
    if (!acknowledged || !fraudPreventionNoticeAccepted || isSubmitting) return

    setSubmitError('')
    setIsSubmitting(true)
    try {
      const response = await createSelfEmploymentAnnualSummary({
        nationalInsuranceNumber: nationalInsuranceNumber.toUpperCase(),
        businessId: selectedBusinessId,
        taxYear: deriveTaxYear(selectedObligation.periodEndDate),
        annualSummary: {
          adjustments: mintedAdjustments,
          allowances: mintedAllowances,
          nonFinancials: mintedNonFinancials
        },
        userAcknowledged: true,
        acknowledgementToken
      })
      // BE6 returns CreateSelfEmploymentSubmissionResponse { success, fraudHeaderValidation }
      // (RF14/FPH-ACC11) — a false `success` means HMRC rejected the submission (the failed
      // attempt is still persisted as an audit row server-side, BE2). fraudHeaderValidation is
      // surfaced via the toast regardless of the success outcome (RF14: it never blocks or
      // otherwise affects the submit flow).
      const success = response && typeof response === 'object' ? response.success : response
      const validation = response && typeof response === 'object' ? response.fraudHeaderValidation : null
      setFraudHeaderValidation(validation || null)
      if (success === false) setSubmitError(GENERIC_SUBMIT_ERROR)
      else setSubmissionSucceeded(true)
    } catch (error) {
      if (error instanceof HmrcTokenExpiredError) setTokenExpired(true)
      else setSubmitError(getApiErrorMessage(error, GENERIC_SUBMIT_ERROR))
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderLoadingOverlay = () => {
    if (!isLoading) return null
    return (
      <MUIStack alignItems="center" sx={{ padding: 4 }} data-testid="AnnualSubmissionModuleLoading">
        <MUICircularProgress />
      </MUIStack>
    )
  }

  const renderNiStep = () => (
    <>
      <MUITypography variant="h6">Enter the National Insurance number</MUITypography>
      <MUITypography variant="body1">
        We will look up the self-employment businesses linked to this National Insurance number via HMRC.
      </MUITypography>

      <NINumberField value={nationalInsuranceNumber} onChange={setNationalInsuranceNumber} required disabled={isLoading} />

      {renderLoadingOverlay()}

      <MUIStack direction="row" justifyContent="flex-end">
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="AnnualSubmissionModule-niNextButton"
          onClick={handleNiNext}
          disabled={!isValidNumber || isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Next'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderBusinessStep = () => (
    <>
      <MUITypography variant="h6">Select a business</MUITypography>
      <MUITypography variant="body1">
        The following self-employment businesses are linked to this National Insurance number.
      </MUITypography>

      {eligibilityError && (
        <MUIAlert severity="error" data-testid="AnnualSubmissionModuleEligibilityError">
          {eligibilityError}
        </MUIAlert>
      )}

      {isLoading ? (
        renderLoadingOverlay()
      ) : (
        <MUIRadioGroup value={selectedBusinessId} onChange={(event) => setSelectedBusinessId(event.target.value)}>
          {businesses.map((business) => (
            <MUIFormControlLabel
              key={business.businessId}
              value={business.businessId}
              control={<MUIRadio />}
              data-testid={`AnnualSubmissionModule-businessOption-${business.businessId}`}
              label={
                <MUIStack sx={{ padding: '8px 0' }}>
                  <MUITypography variant="body1" sx={{ fontWeight: 700 }}>
                    {business.tradingName || 'Unnamed business'}
                  </MUITypography>
                  <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                    Type: {business.typeOfBusiness} | ID: {business.businessId}
                  </MUITypography>
                </MUIStack>
              }
              sx={optionBoxSx}
            />
          ))}
        </MUIRadioGroup>
      )}

      <MUIStack direction="row" justifyContent="space-between">
        <MUIButton variant="outlined" size="large" onClick={handleBusinessBack} disabled={isLoading} sx={{ maxWidth: 120 }}>
          Back
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="AnnualSubmissionModule-businessNextButton"
          onClick={handleBusinessNext}
          disabled={!selectedBusinessId || isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Next'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderObligationStep = () => (
    <>
      <MUITypography variant="h6">Select an obligation</MUITypography>
      <MUITypography variant="body1">
        Select the obligation period this annual summary covers for {selectedBusiness?.tradingName || 'the selected business'}.
      </MUITypography>

      <MUIFormControl sx={{ maxWidth: 250 }}>
        <MUIInputLabel id="annual-obligation-status-filter-label">Filter by status</MUIInputLabel>
        <MUISelect
          labelId="annual-obligation-status-filter-label"
          label="Filter by status"
          data-testid="AnnualSubmissionModule-obligationStatusFilter"
          value={obligationStatusFilter}
          onChange={(event) => handleObligationFilterChange(event.target.value as ObligationStatus | '')}
          disabled={isLoading}
        >
          {OBLIGATION_STATUS_FILTER_OPTIONS.map((option) => (
            <MUIMenuItem key={option.value} value={option.value}>
              {option.label}
            </MUIMenuItem>
          ))}
        </MUISelect>
      </MUIFormControl>

      {isLoading ? (
        renderLoadingOverlay()
      ) : obligations.length === 0 ? (
        <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
          No obligations found for this business.
        </MUITypography>
      ) : (
        <MUIRadioGroup value={selectedObligationIndex} onChange={(event) => setSelectedObligationIndex(event.target.value)}>
          {obligations.map((obligation, index) => (
            <MUIFormControlLabel
              key={`${obligation.businessId}-${obligation.periodStartDate}-${obligation.periodEndDate}`}
              value={String(index)}
              control={<MUIRadio />}
              data-testid={`AnnualSubmissionModule-obligationOption-${index}`}
              label={
                <MUIStack sx={{ padding: '8px 0' }}>
                  <MUITypography variant="body1" sx={{ fontWeight: 700 }}>
                    {obligation.periodStartDate} to {obligation.periodEndDate}
                  </MUITypography>
                  <MUITypography variant="body2" sx={{ color: 'text.secondary' }}>
                    Due: {obligation.dueDate} | Status: {obligation.status}
                    {obligation.receivedDate && ` | Received: ${obligation.receivedDate}`}
                  </MUITypography>
                </MUIStack>
              }
              sx={optionBoxSx}
            />
          ))}
        </MUIRadioGroup>
      )}

      <MUIStack direction="row" justifyContent="space-between">
        <MUIButton
          variant="outlined"
          size="large"
          onClick={handleObligationBack}
          disabled={isLoading}
          sx={{ maxWidth: 120 }}
        >
          Back
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="AnnualSubmissionModule-obligationNextButton"
          onClick={handleObligationNext}
          disabled={!selectedObligation || isLoading}
          sx={{ maxWidth: 200 }}
        >
          Next
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderAdjustmentsStep = () => (
    <>
      <MUITypography variant="h6">Enter adjustments and allowances</MUITypography>
      <MUITypography variant="body1">
        There is no automatic calculation for the annual summary — enter every figure manually for{' '}
        {selectedBusiness?.tradingName || 'the selected business'}. All monetary fields default to £0.00.
      </MUITypography>

      {errorMessage && (
        <MUIAlert severity="error" data-testid="AnnualSubmissionModuleError">
          {errorMessage}
        </MUIAlert>
      )}

      <MUIStack gap={2}>
        <MUITypography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Adjustments
        </MUITypography>
        <MUIStack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
          {ADJUSTMENT_FIELDS.map(({ key, label }) => (
            <TextEntry
              key={key}
              name={key}
              label={label}
              value={adjustments[key as keyof AnnualAdjustments]}
              onChange={(value: number | null) => updateAdjustment(key as keyof AnnualAdjustments, value)}
              isNumber
              decimalPlaces={2}
              adornment="£"
              disabled={isLoading}
              sx={{ width: 'calc(50% - 8px)' }}
            />
          ))}
        </MUIStack>
      </MUIStack>

      <MUIStack gap={2}>
        <MUITypography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Allowances
        </MUITypography>
        <MUIStack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
          {ALLOWANCE_FIELDS.map(({ key, label }) => (
            <TextEntry
              key={key}
              name={key}
              label={label}
              value={allowances[key as keyof AnnualAllowances]}
              onChange={(value: number | null) => updateAllowance(key as keyof AnnualAllowances, value)}
              isNumber
              decimalPlaces={2}
              adornment="£"
              disabled={isLoading}
              sx={{ width: 'calc(50% - 8px)' }}
            />
          ))}
        </MUIStack>
      </MUIStack>

      <MUIStack gap={2}>
        <MUITypography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Non-financials
        </MUITypography>
        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={nonFinancials.businessDetailsChangedRecently}
              onChange={(event) =>
                setNonFinancials((prev) => ({ ...prev, businessDetailsChangedRecently: event.target.checked }))
              }
              disabled={isLoading}
              inputProps={{ 'data-testid': 'AnnualSubmissionModule-businessDetailsChangedCheckbox' }}
            />
          }
          label="Business details changed recently"
        />
        <TextEntry
          name="class4NicsExemptionReason"
          label="Class 4 NICs exemption reason"
          value={nonFinancials.class4NicsExemptionReason || ''}
          onChange={(value: string) =>
            setNonFinancials((prev) => ({ ...prev, class4NicsExemptionReason: value || null }))
          }
          disabled={isLoading}
        />
      </MUIStack>

      {renderLoadingOverlay()}

      <MUIStack direction="row" justifyContent="space-between">
        <MUIButton
          variant="outlined"
          size="large"
          onClick={handleAdjustmentsBack}
          disabled={isLoading}
          sx={{ maxWidth: 120 }}
        >
          Back
        </MUIButton>
        <MUIButton
          variant="contained"
          color="primary"
          size="large"
          data-testid="AnnualSubmissionModule-adjustmentsNextButton"
          onClick={handleAdjustmentsNext}
          disabled={isLoading}
          sx={{ maxWidth: 200 }}
        >
          {isLoading ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Next'}
        </MUIButton>
      </MUIStack>
    </>
  )

  const renderReviewStep = () => {
    if (!mintedAdjustments || !mintedAllowances || !mintedNonFinancials || !selectedObligation) return null
    const taxYear = deriveTaxYear(selectedObligation.periodEndDate)

    return (
      <>
        <MUITypography variant="h6">Review the annual summary</MUITypography>
        <MUIAlert severity="info" sx={{ py: 0.5 }} data-testid="AnnualSubmissionModuleReviewNotice">
          These are the figures the acknowledgement was prepared for. Go back to change any of them.
        </MUIAlert>

        <MUIStack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          <MUITypography variant="body2">
            <strong>Business:</strong> {selectedBusiness?.tradingName || selectedBusinessId}
          </MUITypography>
          <MUITypography variant="body2">
            <strong>Tax year:</strong> {taxYear}
          </MUITypography>
        </MUIStack>

        <FiguresReviewTable title="Adjustments" rows={buildFigureRows(ADJUSTMENT_FIELDS, mintedAdjustments)} />
        <FiguresReviewTable title="Allowances" rows={buildFigureRows(ALLOWANCE_FIELDS, mintedAllowances)} />

        <MUIStack gap={0.5} data-testid="AnnualSubmissionModuleNonFinancials">
          <MUITypography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Non-financials
          </MUITypography>
          <MUITypography variant="body2">
            Business details changed recently: {mintedNonFinancials.businessDetailsChangedRecently ? 'Yes' : 'No'}
          </MUITypography>
          <MUITypography variant="body2">
            Class 4 NICs exemption reason: {mintedNonFinancials.class4NicsExemptionReason || 'None'}
          </MUITypography>
        </MUIStack>

        {submitError && (
          <MUIAlert severity="error" data-testid="AnnualSubmissionModuleSubmitError">
            {submitError}
          </MUIAlert>
        )}

        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
              disabled={isSubmitting}
              inputProps={{ 'data-testid': 'AnnualSubmissionModule-acknowledgeCheckbox' }}
            />
          }
          label={ACKNOWLEDGEMENT_TEXT}
        />

        <MUIFormControlLabel
          control={
            <MUICheckbox
              checked={fraudPreventionNoticeAccepted}
              onChange={(event) => setFraudPreventionNoticeAccepted(event.target.checked)}
              disabled={isSubmitting}
              inputProps={{ 'data-testid': 'AnnualSubmissionModule-fraudPreventionNoticeCheckbox' }}
            />
          }
          label={FRAUD_PREVENTION_NOTICE_TEXT}
        />

        <MUIStack direction="row" justifyContent="space-between">
          <MUIButton
            variant="outlined"
            size="large"
            onClick={handleReviewBack}
            disabled={isSubmitting}
            sx={{ maxWidth: 120 }}
          >
            Back
          </MUIButton>
          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            data-testid="AnnualSubmissionModule-submitButton"
            onClick={handleSubmit}
            disabled={!acknowledged || !fraudPreventionNoticeAccepted || isSubmitting}
            sx={{ maxWidth: 240 }}
          >
            {isSubmitting ? <MUICircularProgress size={24} sx={{ color: 'white' }} /> : 'Submit to HMRC'}
          </MUIButton>
        </MUIStack>
      </>
    )
  }

  const renderReceipt = () => {
    if (!selectedObligation) return null
    const taxYear = deriveTaxYear(selectedObligation.periodEndDate)

    return (
      <MUIStack gap={2} data-testid="AnnualSubmissionModuleReceipt">
        <MUIAlert severity="success">Your annual summary has been submitted to HMRC.</MUIAlert>
        <MUIAlert severity="info" data-testid="AnnualSubmissionModuleFinalDeclarationNotice">
          Annual summary submitted. To finalise your tax year: complete any loss claims, add other income sources, and submit your final declaration in compatible tax software or with your accountant. Osprey covers the self-employment source only.
        </MUIAlert>
        <MUITypography variant="h6">Submission receipt</MUITypography>
        <MUITypography variant="body2">
          <strong>Business:</strong> {selectedBusiness?.tradingName || selectedBusinessId}
        </MUITypography>
        <MUITypography variant="body2">
          <strong>Tax year:</strong> {taxYear}
        </MUITypography>
        <MUITypography variant="body2">
          <strong>Submission type:</strong> Annual summary
        </MUITypography>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="AnnualSubmissionModule"
      gap={4}
      sx={{
        maxWidth: 720,
        margin: 'auto',
        mt: pageTitleBlockIsVisible ? 'auto' : '12rem',
        backgroundColor: 'white',
        borderRadius: '6px',
        px: '40px',
        pb: '60px',
        pt: '40px'
      }}
    >
      <StepIndicator steps={STEPS} currentStepIndex={step} />

      {tokenExpired && (
        <MUIAlert
          severity="warning"
          data-testid="AnnualSubmissionModuleTokenExpiredBanner"
          action={
            <MUIButton
              color="inherit"
              size="small"
              data-testid="AnnualSubmissionModuleReconnectButton"
              onClick={handleReconnect}
            >
              Reconnect to HMRC
            </MUIButton>
          }
        >
          Your HMRC access has expired. Please reconnect your account to continue.
        </MUIAlert>
      )}

      {!tokenExpired && step !== STEP_ADJUSTMENTS && errorMessage && (
        <MUIAlert severity="error" data-testid="AnnualSubmissionModuleError">
          {errorMessage}
        </MUIAlert>
      )}

      {!tokenExpired && step === STEP_NI && renderNiStep()}
      {!tokenExpired && step === STEP_BUSINESS && renderBusinessStep()}
      {!tokenExpired && step === STEP_OBLIGATION && renderObligationStep()}
      {!tokenExpired && step === STEP_ADJUSTMENTS && renderAdjustmentsStep()}
      {!tokenExpired && step === STEP_REVIEW && (submissionSucceeded ? renderReceipt() : renderReviewStep())}

      <FraudHeaderValidationToast validation={fraudHeaderValidation} />
    </MUIStack>
  )
}
